/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { IoDocumentTextOutline } from "react-icons/io5";
import { BsDownload, BsInfoCircle } from "react-icons/bs";
import { TiLockClosed } from "react-icons/ti";
import VehicleCards from "../../cards/VehicleCards";
import VehicleHistory from "../../cards/VehicleHistory";
import useGetCarDetail from "../../../hooks/useGetCarDetail";
import FadeLoader from "react-spinners/FadeLoader";
import SwiperGallery from "./SwiperGallery";
import CurrencyInput from "react-currency-input-field";
import usePlaceBid from "../../../hooks/usePlaceBid";
import useTimer from "../../../hooks/useTimer";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import VehicleCostCalculator from "./VehicleCostCalculator";
import Pusher from "pusher-js";
import ErrorComponent from "./ErrorPage";
import TooltipInfo from "../../common/TooltipInfo";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { statusOptions } from "../../../utils/filtersData/statusOptions";
import { documentTypeOptions } from "../../../utils/filtersData/documentTypeOptions";
import BiddingOverMessage from "./ui/BiddingOverMessage";
import AuctionDateNotDecidedMessage from "./ui/AuctionDateNotDecidedMessage";
import VehicleDetailInfo from "./ui/VehicleDetailInfo";
import SignInModal from "./modals/SignInModal";
import BidModal from "./modals/BidModal";
import CopyURLModal from "./modals/CopyURLModal";
import VehicleTitleInfo from "./ui/VehicleTitleInfo";
import VehcileSellerInfo from "./ui/VehcileSellerInfo";
import VehicleInfoUpperBody from "./ui/VehicleInfoUpperBody";
import SalesHistory from "./SalesHistory";
import useGetCarHistory from "../../../hooks/car_history/useGetCarHistory";
import { documentOldOption } from "../../../utils/filtersData/documentOld";
import AnchorLink from "react-anchor-link-smooth-scroll";
import { useFunds } from "../../../context/FundsContext";
import { useSavedCars } from "../../../context/SavedCarIdsContext";
import useSaveCar from "../../../hooks/useSaveCar";
import useDeleteSaveCar from "../../../hooks/useDeleteSaveCar";
import BuyNowModal from "./modals/BuyNowModal";
import LoginModal from "../../modals/LoginModal";
import ReportPreview from "./report/ReportPreview";
import { ErrorToast } from "../../../utils/ErrorToast";
import useBidHistory from "../../../hooks/useGetBidHistoryByLotId";
import VehicleMetaTags from "../../../utils/VehicleMetaTags";
import { trackViewVehicle } from "../../../utils/metaPixel";

const VehicleHero = () => {
  const { lotID } = useParams();
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const { user } = useAuthContext();
  const userIdRef = useRef(user?.id);
  const { fetchFunds } = useFunds();

  const [isCarSaved, setIsCarSaved] = useState(false);
  const { handleSaveCar } = useSaveCar();
  const { deleteSavedCar } = useDeleteSaveCar();
  const { savedIds, refetchSavedIds } = useSavedCars();
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const winningBid = location?.state?.winningBid;
 

  const { carDetailData, carDetailLoading, carDetailError, fetchCarDetail } =
    useGetCarDetail(`cars/get-car-by-lot-id?lot_id=${lotID}`);

  const { history, fetchHistory } = useGetCarHistory(lotID);
  const { fundsData} = useFunds();

  const [placeBidAmount, setPlaceBidAmount] = useState(0);
  const [showSalesHistory, setShowSalesHistory] = useState(false);
  const toggleSalesHistory = () => {
    setShowSalesHistory((prev) => !prev);
  };

  const { placeBid, placebidLoading, placeBiderror, placeBidSuccess, resetBidState } =
    usePlaceBid();

  const [liveData, setLiveData] = useState({
    currentBid: null,
    noOfBids: null,
    userID: null,
  });

  const [userBidStatus, setUserBidStatus] = useState(null); 
  const [userHasBid, setUserHasBid] = useState(false);

  const { bidHistory, fetchBidHistory } = useBidHistory(lotID);

  const currentBidValue = useMemo(() => {
    const apiBid = Math.max(
      Number(carDetailData?.data?.currentBid || 0),
      Number(carDetailData?.data?.current_bid || 0)
    );
    const liveBid = Number(liveData?.currentBid || 0);
    return Math.max(apiBid, liveBid);
  }, [carDetailData?.data?.currentBid, carDetailData?.data?.current_bid, liveData?.currentBid]);

  // Prefer server's current_bid; fallback to currentBid if needed (used for post-1min display)
  const apiCurrentBidValueStrict = useMemo(() => {
    const snake = Number(carDetailData?.data?.current_bid);
    if (Number.isFinite(snake) && snake > 0) return snake;
    const camel = Number(carDetailData?.data?.currentBid);
    return Number.isFinite(camel) ? camel : null;
  }, [carDetailData?.data?.current_bid, carDetailData?.data?.currentBid]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    const pusher = new Pusher(process.env.REACT_APP_PUSHER_APP_ID, {
      cluster: "ap2",
    });

    const channel = pusher.subscribe(`public-notification-${lotID}`);

    channel.bind(`car-notifications`, (data) => {
      setLiveData({
        currentBid: data.message.bid_price,
        noOfBids: data.message.noOfBids,
        userID: data.message.userID,
      });

      const { userID } = data?.message || {};
      const currentUserId = userIdRef.current;
      const isOtherUserBid =
        userID && currentUserId && userID.toString() !== currentUserId.toString();

      if (isOtherUserBid) {
        toast.success("Someone placed new bid");
      }
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  }, [lotID]);

  useEffect(() => {
    if (lotID) {
      fetchBidHistory();
    }
  }, [lotID, fetchBidHistory]);

  const bidFetchTimeoutRef = useRef(null);

  useEffect(() => {
    if (liveData.currentBid != null) {
      if (bidFetchTimeoutRef.current) {
        clearTimeout(bidFetchTimeoutRef.current);
      }
      bidFetchTimeoutRef.current = setTimeout(() => {
        fetchBidHistory();
      }, 400);
    }
    return () => {
      if (bidFetchTimeoutRef.current) {
        clearTimeout(bidFetchTimeoutRef.current);
      }
    };
  }, [liveData.currentBid, fetchBidHistory]);

  const handlePlaceBid = () => {
    if (!user) {
      document.getElementById("sign_in_modal").showModal();
    } else {
      document.getElementById("my_modal_2").showModal();
    }
  };

  const handlePlaceBuyNow = () => {
    if (!user) {
      document.getElementById("sign_in_modal").showModal();
    } else {
      document.getElementById("buy_now_modal").showModal();
    }
  };

  const handleCloseModal2 = () => {
    document.getElementById("my_modal_2").close();
  };

  const handleCloseModal3 = () => {
    document.getElementById("buy_now_modal").close();
  };

  const handleBidPlace = async () => {
    const placeBidAmountConvert = parseInt(placeBidAmount, 10);
    await placeBid({ lot_id: lotID, currentBid: placeBidAmountConvert });
    await fetchFunds();
  };

  const handleBuyNowBid = async (buynowprice) => {
    await placeBid({ lot_id: lotID, currentBid: buynowprice });
    await fetchFunds();
    document.getElementById("buy_now_modal").close();
  };

  const targetTime = useMemo(
    () =>
      carDetailData?.data?.auction_date
        ? new Date(carDetailData?.data?.auction_date)
        : null,
    [carDetailData?.data?.auction_date]
  );
  const { days, hours, minutes, seconds } = useTimer(targetTime);

  const ValidDate =
    targetTime && (days > 0 || hours > 0 || minutes > 0 || seconds > 0);

  // New: treat auction as ended if Buy Now is met OR a final bid price exists (> 0)
  const finalBidPriceValue = useMemo(() => {
    const camel = Number(carDetailData?.data?.finalBidPrice || 0);
    const snake = Number(carDetailData?.data?.final_bid_price || 0);
    return Math.max(camel, snake);
  }, [carDetailData?.data?.finalBidPrice, carDetailData?.data?.final_bid_price]);

  const hasFinalBidPrice = finalBidPriceValue > 0;

  // Resolve Buy Now price from multiple possible keys (handles snake/camel and variants)
  const buyNowPriceValue = useMemo(() => {
    const candidates = [
      carDetailData?.data?.price_new,
      carDetailData?.data?.priceNew,
      carDetailData?.data?.buy_now_price,
      carDetailData?.data?.buyNowPrice,
      carDetailData?.data?.buy_now,
      carDetailData?.data?.buyNow,
    ];
    for (const raw of candidates) {
      const num = Number(raw);
      if (Number.isFinite(num) && num > 0) return num;
    }
    return null;
  }, [carDetailData?.data]);

  const buyNowMet = useMemo(() => {
    return buyNowPriceValue != null && currentBidValue >= buyNowPriceValue;
  }, [buyNowPriceValue, currentBidValue]);

  const auctionEnded = !ValidDate || buyNowMet || hasFinalBidPrice;

  // After auction ends, if final price is not available, reveal current bid after 6 hours (persist across reloads)
  const [showFinalFallback, setShowFinalFallback] = useState(false);
  useEffect(() => {
    let timerId;
    const fallbackDelayMs = 21600000; // 6 hours

    // Cleanup helper
    const clearTimer = () => {
      if (timerId) clearTimeout(timerId);
    };

    if (!auctionEnded || hasFinalBidPrice) {
      setShowFinalFallback(false);
      clearTimer();
      return () => clearTimer();
    }

    const now = Date.now();

    if (!ValidDate && targetTime instanceof Date) {
      const endMs = targetTime.getTime();
      const elapsed = now - endMs;
      if (elapsed >= fallbackDelayMs) {
        setShowFinalFallback(true);
      } else {
        setShowFinalFallback(false);
        timerId = setTimeout(() => setShowFinalFallback(true), Math.max(fallbackDelayMs - Math.max(elapsed, 0), 0));
      }
    }
    // Case 2: Ended by Buy Now being met. Persist first detection timestamp per lot in localStorage.
    else if (buyNowMet) {
      const storageKey = `buyNowMetAt:${lotID}`;
      let ts = Number(localStorage.getItem(storageKey));
      if (!Number.isFinite(ts) || ts <= 0) {
        ts = now;
        try {
          localStorage.setItem(storageKey, String(ts));
        } catch (_) {
        }
      }
      const elapsed = now - ts;
      if (elapsed >= fallbackDelayMs) {
        setShowFinalFallback(true);
      } else {
        setShowFinalFallback(false);
        timerId = setTimeout(() => setShowFinalFallback(true), Math.max(fallbackDelayMs - Math.max(elapsed, 0), 0));
      }
    } else {
      setShowFinalFallback(false);
      clearTimer();
    }

    return () => clearTimer();
  }, [auctionEnded, hasFinalBidPrice, buyNowMet, ValidDate, targetTime, lotID]);

  // Clear persisted buy-now timestamp when final price becomes available
  useEffect(() => {
    if (hasFinalBidPrice) {
      try {
        localStorage.removeItem(`buyNowMetAt:${lotID}`);
      } catch (_) {
        // ignore storage errors
      }
    }
  }, [hasFinalBidPrice, lotID]);

  // Show green LIVE button when primary bidding time is over but final price not yet available (until fallback kicks in)
  const showLiveBadge = useMemo(() => {
    return (!ValidDate || buyNowMet) && !hasFinalBidPrice && !showFinalFallback;
  }, [ValidDate, buyNowMet, hasFinalBidPrice, showFinalFallback]);

  const userLastBidPrice = useMemo(() => {
    try {
      if (!bidHistory?.data || !user?.id) return null;
      const userIdString = user.id.toString();
      const entries = bidHistory.data.filter(
        (e) => e?.bid?.userID?.toString() === userIdString
      );
      if (!entries.length) return null;

      const enhanced = entries.map((e) => {
        const ts = e?.bid?.createdAt || e?.bid?.created_at || e?.createdAt || e?.created_at;
        const time = ts ? new Date(ts).getTime() : NaN;
        return { entry: e, time };
      });
      const hasValidTime = enhanced.some((x) => !isNaN(x.time));
      let lastEntry;
      if (hasValidTime) {
        lastEntry = enhanced.reduce((a, b) => (b.time > a.time ? b : a)).entry;
      } else {
        lastEntry = entries[entries.length - 1];
      }
      const price = Number(lastEntry?.bid?.bidPrice);
      return Number.isFinite(price) ? price : null;
    } catch (_) {
      return null;
    }
  }, [bidHistory?.data, user?.id]);

  const finalDisplayPrice = useMemo(() => {
    if (hasFinalBidPrice) return finalBidPriceValue;
    if (userLastBidPrice != null) return userLastBidPrice;
    return currentBidValue;
  }, [hasFinalBidPrice, finalBidPriceValue, userLastBidPrice, currentBidValue]);

  const userHasWinningBid = useMemo(() => {
    if (!user?.id || !bidHistory?.data) return false;
    const userIdString = user.id.toString();
    return bidHistory.data.some(
      (e) => e?.bid?.userID?.toString() === userIdString && e?.bid?.winningBid === true
    );
  }, [user?.id, bidHistory?.data]);

  // Determine if the latest bid (globally) is invalid and not a winning bid
  const latestGlobalBidEntry = useMemo(() => {
    try {
      const entries = bidHistory?.data;
      if (!entries || !entries.length) return null;

      const enhanced = entries.map((e) => {
        const ts = e?.bid?.createdAt || e?.bid?.created_at || e?.createdAt || e?.created_at;
        const time = ts ? new Date(ts).getTime() : NaN;
        return { entry: e, time };
      });
      const hasValidTime = enhanced.some((x) => !isNaN(x.time));
      if (hasValidTime) {
        return enhanced.reduce((a, b) => (b.time > a.time ? b : a)).entry;
      }
      return entries[entries.length - 1];
    } catch (_) {
      return null;
    }
  }, [bidHistory?.data]);

  const showGlobalLostBanner = useMemo(() => {
    const bid = latestGlobalBidEntry?.bid;
    return (
      auctionEnded &&
      bid &&
      bid.isValid === false &&
      bid.winningBid === false
    );
  }, [auctionEnded, latestGlobalBidEntry]);

  useEffect(() => {
    if (currentBidValue) {
      setPlaceBidAmount(currentBidValue + 500);
    }
  }, [currentBidValue]);

  useEffect(() => {
    fetchCarDetail();
  }, [lotID, fetchCarDetail]);

  // Meta Pixel: tell Meta which car was viewed (content_ids must match the catalog feed id = lot_id).
  const trackedViewLot = useRef(null);
  useEffect(() => {
    const car = carDetailData?.data;
    if (!lotID || !car || trackedViewLot.current === lotID) return;
    trackedViewLot.current = lotID;
    trackViewVehicle(lotID, car);
  }, [lotID, carDetailData]);

  useEffect(() => {
    fetchHistory();
  }, [lotID, fetchHistory]);

  useEffect(() => {
    if (shouldRefetch) {
      fetchCarDetail();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, fetchCarDetail]);

  useEffect(() => {
    if (placeBidSuccess) {
      setShouldRefetch(true);
      toast.success("Bid has been placed successfully");
      document.getElementById("my_modal_2").close();
      resetBidState();
    }

    if (placeBiderror) {
      toast.error(placeBiderror);
      // console.log("placeBiderror", placeBiderror);

      if (placeBiderror === "Your document verification is pending") {
        ErrorToast(
          "Your document verification is pending. Please click here to upload your documents.",
          () => {
            navigate("/user/documents-upload");
          }
        )
      }
      resetBidState();
    }
  }, [placebidLoading, placeBidSuccess, placeBiderror, navigate, resetBidState]);

  

  useEffect(() => {
    setIsCarSaved(savedIds?.data && savedIds?.data.includes(String(lotID)));
  }, [savedIds, lotID]);

  const handleSaveClick = (lot_id) => {
    const stringLotId = String(lot_id);

    if (!user) {
      setLoginModalOpen(true);
      return;
    }

    if (isCarSaved) {
      setIsCarSaved(false);
      deleteSavedCar(stringLotId)
        .then(() => {
          refetchSavedIds();
          toast.success("Car unsaved successfully");
        })
        .catch(() => {
          setIsCarSaved(true);
          toast.error("Failed to unsave car. Please try again.");
        });
    } else {
      setIsCarSaved(true);
      handleSaveCar(stringLotId)
        .then(() => {
          refetchSavedIds();
          toast.success("Car saved successfully");
        })
        .catch(() => {
          setIsCarSaved(false);
          toast.error("Failed to save car. Please try again.");
        });
    }
  };

  const currentStatus = statusOptions.find(
    (option) => option.id === carDetailData?.data?.status
  );
  const currentDocumentType = documentTypeOptions.find(
    (option) =>
      option.id.toLowerCase() === carDetailData?.data?.document?.toLowerCase()
  );

  const currentDocumentOldType = documentOldOption.find(
    (option) =>
      option.id.toLowerCase() ===
      carDetailData?.data?.document_old?.toLowerCase()
  );

  const closeLoginModal = () => {
    setLoginModalOpen(false);
  };

  const totalFunds = fundsData?.data?.totalDeposits || 0;

  useEffect(() => {
    if (!user || !bidHistory?.data) {
      setUserHasBid(false);
      setUserBidStatus(null);
      return;
    }

    const entries = bidHistory.data;
    const userIdString = user?.id?.toString();
    const userBids = entries.filter(
      (e) => e?.bid?.userID?.toString() === userIdString
    );

    if (!userBids.length) {
      setUserHasBid(false);
      setUserBidStatus(null);
      return;
    }

    setUserHasBid(true);

    const highestOverall = entries.reduce((max, e) => {
      const price = Number(e?.bid?.bidPrice) || 0;
      return price > max ? price : max;
    }, 0);
    const userHighest = userBids.reduce((max, e) => {
      const price = Number(e?.bid?.bidPrice) || 0;
      return price > max ? price : max;
    }, 0);

    const userHasActive = userBids.some((e) => Boolean(e?.bid?.isValid));

    if (auctionEnded) {
      const anyWinningEntry = entries.some((e) => e?.bid?.winningBid === true);
      if (anyWinningEntry) {
        setUserBidStatus(userHasWinningBid ? "won" : "lost");
        return;
      }
      if (userHighest > 0 && userHighest === highestOverall) {
        setUserBidStatus("winning");
      } else {
        setUserBidStatus("lost");
      }
      return;
    }

    if (liveData?.userID && userIdString) {
      setUserBidStatus(
        liveData.userID.toString() === userIdString ? "winning" : "outbid"
      );
    } else {
      setUserBidStatus(userHasActive ? "winning" : "outbid");
    }
  }, [user, bidHistory, auctionEnded, liveData?.userID, hasFinalBidPrice, userHasWinningBid]);

  return (
    <div className="bg-gray-100 ">
      {carDetailData?.data && (
        <VehicleMetaTags carData={{
          car: carDetailData?.data,
          carType: "import"
        }} />
      )}
      
      <div className="hidden lg:block vehicle-detail">
        <div className="w-[15.5] flex flex-col pt-[20vh]">
          <div className="text-[2.6vw] font-semibold text-white">
            Vehicle Details
          </div>
          <div className="text-white flex gap-3 justify-center text-[1vw] font-urbanist">
            <Link to="/">
              <button className="hover:text-white hover:scale-110 duration-150">
                Home
              </button>
            </Link>
            /
            <button className="hover:text-white hover:scale-110 duration-150">
              Vehicle Details
            </button>
          </div>
        </div>
      </div>

      {carDetailLoading ? (
        <div className="w-[100vw] h-[100vh] flex justify-center items-center">
          <FadeLoader />
        </div>
      ) : (
        <>
          {carDetailData && (
            <div className="mt-[80px] lg:mt-0">
              {carDetailData.data.auction_date ? (
                !auctionEnded && ValidDate ? (
                  ``
                ) : (
                  <BiddingOverMessage />
                )
              ) : (
                <AuctionDateNotDecidedMessage />
              )}

              <div className=" lg:p-[0.25vw] mx-auto mt-[10px] lg:mt-[1vw] bg-white w-[90vw] lg:w-[85vw]">
                <button
                  onClick={toggleSalesHistory}
                  className="lg:py-[0.5vw] py-2 px-4 lg:px-[0.75vw] border text-[14px] lg:text-[1vw] flex items-center justify-center gap-x-1"
                >
                  <span>
                    {showSalesHistory ? "Sales History" : "Sales History"}
                  </span>
                  <span>{history?.data?.length}</span>
                </button>
              </div>

              <div className="mx-auto w-[90vw] lg:w-[85vw] mb-4">
                {showSalesHistory && <SalesHistory history={history?.data} />}
              </div>

              <div className="flex flex-col lg:flex-row justify-between mx-auto w-[95%] sm:max-w-[85vw]">
                <div className="w-full lg:w-[48%]">
                  <div className="w-full ">
                    <SwiperGallery
                      images={carDetailData?.data?.link_img_hd}
                      carData={carDetailData?.data}
                    />
                  </div>
                  <AnchorLink
                    href="#get_report-mobile"
                    className="block lg:hidden"
                  >
                    <div className="flex justify-between my-2 lg:my-0  px-2 items-center w-full border text-primary-red border-primary-red md:text-[1.04vw] h-[4.7vh] rounded-lg">
                      <div className="flex justify-center items-center gap-1 lg:gap-[0.25vw]">
                        <IoDocumentTextOutline />
                        <p>Get Report</p>
                      </div>
                      <BsDownload className="cursor-pointer" />
                    </div>
                  </AnchorLink>

                  <AnchorLink
                    href="#get_report-web"
                    className="hidden lg:block my-2"
                  >
                    <div className="flex justify-between my-2 lg:my-0  px-2 items-center w-full border text-primary-red border-primary-red md:text-[1.04vw] h-[4.7vh] rounded-lg">
                      <div className="flex justify-center items-center gap-1 lg:gap-[0.25vw]">
                        <IoDocumentTextOutline />
                        <p>Get Report</p>
                      </div>
                      <BsDownload className="cursor-pointer" />
                    </div>
                  </AnchorLink>

                  <div className="hidden lg:block">
                    <VehicleDetailInfo
                      data={carDetailData?.data}
                      currentStatus={currentStatus}
                      currentDocumentOldType={currentDocumentOldType}
                      salesHistoryCount={history?.data?.length}
                      auctionEnded={auctionEnded}
                    />
                  </div>
                </div>

                {/* web view */}
                <div className="  lg:w-[48%]   ">
                  <div className=" ">
                    {/* Title Info */}
                    <VehicleTitleInfo
                      lotID={lotID}
                      user={user}
                      isCarSaved={isCarSaved}
                      handleSaveClick={handleSaveClick}
                      currentStatus={currentStatus}
                      title={carDetailData?.data?.title}
                      baseSite={carDetailData?.data?.base_site}
                      priceNew={buyNowPriceValue}
                      winningBid={winningBid}
                    />

                    {user && showGlobalLostBanner && (
                      <div className="mt-2 mb-2 p-3 text-center font-semibold rounded-md text-white bg-red-600">
                        You Lost the Auction
                      </div>
                    )}

{user && userHasBid && userBidStatus && !showGlobalLostBanner && (
  <div
    className={`mt-2 mb-2 p-3 text-center font-semibold rounded-md text-white ${
      userBidStatus === "winning"
        ? "bg-green-600"
        : userBidStatus === "outbid"
        ? "bg-orange-500"
        : userBidStatus === "won"
        ? "bg-green-700"
        : "bg-red-600"
    }`}
  >
    {userBidStatus === "winning" && "You are currently Winning"}

    {userHasWinningBid &&
      "You Won the Auction! 🎉"}

    {/* Fallback for active auctions */}
    {userBidStatus === "outbid" && "You have been Outbidded"}
    {userBidStatus === "lost" && "You Lost the Auction"}
  </div>
)}


                    <VehcileSellerInfo
                      seller={carDetailData?.data?.seller}
                      seller_detailed_type={carDetailData?.data?.seller_detailed_type}
                      seller_type={carDetailData?.data?.seller_type}  
                      documentOld={carDetailData?.data?.document_old}
                      document={carDetailData?.data?.document}
                      documentOldType={currentDocumentOldType}
                      currentDocumentType={currentDocumentType}
                    />
                    <VehicleInfoUpperBody
                      carDetailData={carDetailData}
                      liveData={liveData}
                      currentBidValue={currentBidValue}
                      days={days}
                      hours={hours}
                      minutes={minutes}
                      seconds={seconds}
                      ValidDate={ValidDate}
                      auctionEnded={auctionEnded}
                      winningBid={winningBid}
                    />

                    <div className="flex font-urbanist lg:text-18 p-2 lg:p-[0.6vw] bg-white w-full mt-2 lg:mt-[0.5vw] lg:rounded-[0.5vw] gap-x-2 lg:gap-x-[0.5vw] rounded-md items-center ">
                      <span className=" text-left font-semibold ">
                        {"Estimation Cost"}
                      </span>
                      <span>{carDetailData?.data?.estimatedPricesRange}</span>
                    </div>
                  </div>
                  
                  {ValidDate && (!carDetailData?.data?.price_new || currentBidValue < carDetailData.data.price_new) && (
                  <div className="mt-6">
                    <label className="block lg:text-[0.875vw] font-medium text-gray-900 mb-[1vh]">
                      Enter Your Final Bid
                    </label>
                    <CurrencyInput
                      id="input-example"
                      name="minPrice"
                      placeholder="enter your bid amount in usd"
                      prefix="$"
                      className={`border lg:text-[1vw] py-[0.9vh] px-[1vw] rounded-[0.5vw] w-full mt-[1.5vh]`}
                      defaultValue={0}
                      decimalsLimit={2}
                      value={placeBidAmount}
                      onValueChange={(value) => setPlaceBidAmount(value)}
                    />
                  </div>
                  )}

                  <button
                    onClick={handlePlaceBid}
                    className={`flex justify-center mt-[2.167vh] items-center gap-x-[0.5vw] h-[5.4vh] text-lg mb-[2.167vh] rounded-[0.7vw] text-white font-semibold w-full ${
                      auctionEnded
                        ? "bg-gray-500 cursor-not-allowed opacity-50"
                        : placeBidAmount <= currentBidValue
                          ? "bg-red-600 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                    }`}
                    disabled={auctionEnded || placeBidAmount <= currentBidValue}
                  >
                    {placebidLoading ? (
                      <ClipLoader color="#ffffff" size={20} />
                    ) : (
                      <>
                        <TiLockClosed className="lg:w-[1.3vw] lg:h-[2.8vh]" />
                        <span className="text-md lg:text-[1.1vw]">
                          {auctionEnded ? "AUCTION ENDED" : "PLACE MAX BID"}
                        </span>
                      </>
                    )}
                    {placeBidAmount <= currentBidValue && !auctionEnded && (
                      <TooltipInfo content="please place higher than current bid">
                        <BsInfoCircle
                          size={15}
                          className="hover:text-blue-800 duration-200"
                        />
                      </TooltipInfo>
                    )}
                  </button>

                  {carDetailData?.data?.is_buynow && (
                    showLiveBadge ? (
                      <div>
                        <TooltipInfo content="The Preliminary Bidding is over!">
                          <button
                            disabled
                            className={"flex justify-start mt-[2.167vh] px-[19vw] items-center gap-x-[0.5vw] h-[5.4vh] text-lg mb-[2.167vh] rounded-[0.7vw] text-white font-semibold w-full bg-green-600 hover:bg-green-700 cursor-not-allowed"}
                          >
                            <span className="text-md lg:text-[1.1vw]">LIVE</span>
                          </button>
                        </TooltipInfo>
                        <span className="flex items-center gap-1 text-xs text-gray-600 hover:text-yellow-500 text-start">
                          <BsInfoCircle size={12} className="inline-block" />
                          The Preliminary Bidding is over — The car is now up for the Live Auction!
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={handlePlaceBuyNow}
                        className={`flex justify-center mt-[2.167vh] items-center gap-x-[0.5vw] h-[5.4vh] text-lg mb-[2.167vh] rounded-[0.7vw] text-white font-semibold duration-300 transform w-full ${
                          !ValidDate
                            ? "bg-gray-500 cursor-not-allowed opacity-50"
                            : (buyNowMet || hasFinalBidPrice)
                              ? "bg-red-600 cursor-not-allowed"
                              : "bg-gradient-to-l from-green-700 to-green-600 hover:opacity-90"
                        }`}
                        disabled={!ValidDate || buyNowMet || hasFinalBidPrice}
                      >
                        <>
                          <span className="text-md lg:text-[1.1vw]">
                            {!ValidDate || buyNowMet || hasFinalBidPrice ? "FINAL BID PRICE" : "BUY NOW"}
                          </span>
                          <span className="text-md lg:text-[1.1vw]">
                            {auctionEnded
                              ? (hasFinalBidPrice ? finalDisplayPrice : (apiCurrentBidValueStrict ?? currentBidValue)).toLocaleString("en-US", {

                                  style: "currency",
                                  currency: "USD",
                                })
                              : buyNowPriceValue != null
                                ? buyNowPriceValue.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  })
                                : "N/A"}
                          </span>
                        </>
                      </button>
                    )
                  )}

                  {/* Show LIVE or final price area for non-buy-now cars when auction ends */}
                  {!carDetailData?.data?.is_buynow && auctionEnded && (
                    (hasFinalBidPrice || showFinalFallback) ? (
                      <button
                        disabled
                        className={
                          "flex justify-center mt-[2.167vh] items-center gap-x-[0.5vw] h-[5.4vh] text-lg mb-[2.167vh] rounded-[0.7vw] text-white font-semibold w-full bg-gray-500 cursor-not-allowed opacity-50"
                        }
                      >
                        <span className="text-md lg:text-[1.1vw]">FINAL BID PRICE</span>
                        <span className="text-md lg:text-[1.1vw]">
                          {(hasFinalBidPrice ? finalDisplayPrice : (apiCurrentBidValueStrict ?? currentBidValue)).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </span>
                      </button>
                    ) : (
                      <div>
                        <TooltipInfo content="The Preliminary Bidding is over!">
                          <button
                            disabled
                            className={
                              "flex justify-start mt-[2.167vh] items-center px-[19vw] gap-x-[0.5vw] h-[5.4vh] text-lg mb-[2.167vh] rounded-[0.7vw] text-white font-semibold w-full bg-green-600 hover:bg-green-700 cursor-not-allowed"
                            }
                          >
                            <span className="text-md lg:text-[1.1vw]">LIVE</span>
                          </button>
                        </TooltipInfo>
                        <span className="flex items-center gap-1 text-xs text-gray-600 hover:text-yellow-500 text-start">
                          <BsInfoCircle size={12} className="inline-block" />
                          The Preliminary Bidding is over — The car is now up for the Live Auction!
                        </span>
                      </div>
                    )
                  )}

                  <div className="">
                    <VehicleCostCalculator
                      data={carDetailData?.data}
                      bidAmount={placeBidAmount}
                    />
                  </div>

                  <div
                    id="get_report-web"
                    className="hidden lg:block flex-col justify-center w-full gap-y-4"
                  >
                    <ReportPreview vin={carDetailData?.data?.vin} />
                  </div>

                  <div className="lg:hidden block">
                    <VehicleDetailInfo
                      data={carDetailData?.data}
                      currentStatus={currentStatus}
                      currentDocumentOldType={currentDocumentOldType}
                      salesHistoryCount={history?.data?.length}
                      auctionEnded={auctionEnded}
                    />
                  </div>
                  <div
                    id="get_report-mobile"
                    className="flex lg:hidden  flex-col justify-center max-w-[700px] gap-y-4"
                  >
                    <ReportPreview vin={carDetailData?.data?.vin} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {carDetailError && (
        <div className="flex justify-center items-start min-h-[80vh] pt-24">
          <ErrorComponent carDetailError={carDetailError} param={lotID} />
        </div>
      )}

      {carDetailData && (
        <div className=" md:mt-20 mt-20">
          <VehicleCards carData={carDetailData.data} />
        </div>
      )}

      {carDetailData && (
        <div className=" md:mt-20 mt-20">
          <VehicleHistory carData={carDetailData.data} />
        </div>
      )}

      <LoginModal
        isOpen={isLoginModalOpen && !user}
        onClose={closeLoginModal}
      />
      <SignInModal
        closeModal={() => document.getElementById("sign_in_modal").close()}
      />
      <CopyURLModal
        closeModal={() => document.getElementById("copy_url_modal").close()}
      />

      <BidModal
        currentBid={currentBidValue}
        liveData={liveData?.currentBid}
        totalFunds={totalFunds}
        isLoading={placebidLoading}
        onBidPlace={handleBidPlace}
        onClose={handleCloseModal2}
        placeBidAmount={placeBidAmount}
      />

      <BuyNowModal
        totalFunds={totalFunds}
        isLoading={placebidLoading}
        onBidPlace={() => handleBuyNowBid(carDetailData?.data?.price_new)}
        onClose={handleCloseModal3}
        placeBuyAmount={carDetailData?.data?.price_new}
      />
    </div>
  );
};

export default VehicleHero;
